import { 
  Component, 
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  Inject,
  forwardRef
} from '@angular/core';

import { 
  FormGroup,
  Validators 
} from '@angular/forms';

import { isEqual, isNil } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil, mergeMap, shareReplay } from 'rxjs/operators';

import { DatexFormControl, validateControlOnChange, validateFormOnControlChange } from './models/datex-form-control';
import { 
  TextBoxModel, 
  NumberBoxModel, 
  SelectBoxModel, 
  ESelectBoxType,
  DateBoxModel, 
  CheckBoxModel, 
  TextModel, 
  LabelModel, 
  ButtonModel,
  SplitButtonModel,
  SeparatorModel,
  ImageModel,
  DrawModel,
  CodeBoxModel,
  ButtonStyles,
  ValueControlModel
} from './models/control';
import { Styles, ControlContainerStyles } from './models/style';
import { FieldModel } from './models/field';
import { FieldsetModel } from './models/fieldset';
import { ToolModel } from './models/tool';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { PackVerification_ShellService } from './PackVerification.shell.service';
import { PackVerification_OperationService } from './PackVerification.operation.service';
import { PackVerification_DatasourceService } from './PackVerification.datasource.index';
import { PackVerification_FlowService } from './PackVerification.flow.index';
import { PackVerification_ReportService } from './PackVerification.report.index';
import { PackVerification_LocalizationService } from './PackVerification.localization.service';
import { PackVerification_configurations_form_ComponentContextService } from './PackVerification.configurations_form.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './PackVerification.frontend.types'
import { $frontendTypes as $types} from './PackVerification.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';

@Component({
  standalone: true,
  imports: [
    SharedModule,
  ],
  selector: 'PackVerification-configurations_form',
  templateUrl: './PackVerification.configurations_form.component.html'
})
export class PackVerification_configurations_formComponent extends BaseComponent implements OnInit, OnDestroy {


  //#region Outputs
  @Output()
  $finish = new EventEmitter();
  outParams: { is_confirmed?: boolean } = { is_confirmed: null };
  //#endregion

  //#region title
  // Make it async so that it won't cause expressionChangedAfterItHasBeenCheckedError
  // The title is often meant to be shown from the parent (shell breadcrumb for example)
  // and often it will cause an expressionChangedAfterItHasBeenCheckedError because 
  // the parent has already been checked and the child now change something on the parent 
  // in dev, CD is run twice
  $titleChange = new EventEmitter<string>(true);
  private $_title: string;
  get title(): string {
    return this.$_title;
  }
  set title(t: string) {
    this.$_title = t;
    this.$titleChange.emit(this.$_title);
  }
  //#endregion title
  //#region Variables
  vars: { entity?: any } = { };
  //#endregion
  //#region Events
  @Output()
  outParamsChange = new EventEmitter<{ is_confirmed?: boolean }>();
  
  events = {
    outParamsChange: { emit: () => { this.outParamsChange.emit(this.outParams); } }
  }
  //#endregion

  formGroup: FormGroup = new FormGroup({
    weight_variance_threshold_percent: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
    require_container_type: new DatexFormControl(null, { validators: [  ], updateOn: 'change' }),
    require_complete_all_instructions: new DatexFormControl(null, { validators: [  ], updateOn: 'change' }),
    can_continue_closed_sessions: new DatexFormControl(null, { validators: [  ], updateOn: 'change' }),
    auto_print_reports_on_complete: new DatexFormControl(null, { validators: [  ], updateOn: 'change' }),
  });

  get valid(): boolean {
    return this.formGroup.valid;
  }
  
  toolbar = {
      confirm: new ToolModel(new ButtonModel('confirm', new ButtonStyles(['primary'], null), true, false, false, 'Save', '', null)
    , false),
      cancel: new ToolModel(new ButtonModel('cancel', new ButtonStyles(null, null), false, false, false, 'Cancel', '', null)
    , false)
  };

  fields = {
    weight_variance_threshold_percent: new FieldModel(new NumberBoxModel(this.formGroup.controls['weight_variance_threshold_percent'] as DatexFormControl, null, false, false, '', '', null)
, new ControlContainerStyles(null, null), 'Weight variance threshold', false, false),
    require_container_type: new FieldModel(new CheckBoxModel(this.formGroup.controls['require_container_type'] as DatexFormControl, null, false, false, 'Require a container type', null)
, new ControlContainerStyles(null, null), '', false, false),
    require_complete_all_instructions: new FieldModel(new CheckBoxModel(this.formGroup.controls['require_complete_all_instructions'] as DatexFormControl, null, false, false, 'Require all instructions to be completed', null)
, new ControlContainerStyles(null, null), '', false, false),
    can_continue_closed_sessions: new FieldModel(new CheckBoxModel(this.formGroup.controls['can_continue_closed_sessions'] as DatexFormControl, null, false, false, 'Can continue closed sessions', null)
, new ControlContainerStyles(null, null), '', false, false),
    auto_print_reports_on_complete: new FieldModel(new CheckBoxModel(this.formGroup.controls['auto_print_reports_on_complete'] as DatexFormControl, null, false, false, 'Auto-print reports on completion of pack verification', null)
, new ControlContainerStyles(null, null), '', false, false),
  };

  fieldsets = {
    header: new FieldsetModel(
      'Header',
      true,
      false,
      true,
      false,
        null
      ,
      () => {
        const $form = this;
        const $utils = this.$utils;
        return '';
      }
    ),
};

  //#region fields inParams
  //#endregion fields inParams

  $formGroupFieldValidationObservables = {
    weight_variance_threshold_percent: this.fields.weight_variance_threshold_percent.control.valueChanges
    .pipe(mergeMap(() => validateFormOnControlChange(this.formGroup, async () => {
    let data: any = null;
    return  await this.validate_form(data);
  }
  ))),
    require_container_type: this.fields.require_container_type.control.valueChanges
    .pipe(mergeMap(() => validateFormOnControlChange(this.formGroup, async () => {
    let data: any = null;
    return  await this.validate_form(data);
  }
  ))),
    require_complete_all_instructions: this.fields.require_complete_all_instructions.control.valueChanges
    .pipe(mergeMap(() => validateFormOnControlChange(this.formGroup, async () => {
    let data: any = null;
    return  await this.validate_form(data);
  }
  ))),
    can_continue_closed_sessions: this.fields.can_continue_closed_sessions.control.valueChanges
    .pipe(mergeMap(() => validateFormOnControlChange(this.formGroup, async () => {
    let data: any = null;
    return  await this.validate_form(data);
  }
  ))),
    auto_print_reports_on_complete: this.fields.auto_print_reports_on_complete.control.valueChanges
    .pipe(mergeMap(() => validateFormOnControlChange(this.formGroup, async () => {
    let data: any = null;
    return  await this.validate_form(data);
  }
  ))),
  }
  

  constructor(
    private $utils: UtilsService,
private $settings: SettingsValuesService,
private $shell: PackVerification_ShellService,
private $datasources: PackVerification_DatasourceService,
private $flows: PackVerification_FlowService,
private $reports: PackVerification_ReportService,
private $localization: PackVerification_LocalizationService,
private $operations: PackVerification_OperationService,
private $logger: CleanupLoggerService,
private $context: PackVerification_configurations_form_ComponentContextService,
) { 
    super();
    this.$subscribeFormControlValueChanges();
  }

  ngOnInit(): void {
    this.$init();
  }
  

  private $unsubscribe$ = new Subject();
  ngOnDestroy(): void {
    this.$unsubscribe$.next(null);
    this.$unsubscribe$.complete();
  }

  initialized = false;

  async $init() {
    this.title = 'Configurations';
  
    const $form = this;
    const $utils = this.$utils;


    await this.on_init();

    this.initialized = true;
  }

  private $subscribeFormControlValueChanges() {
    this.$formGroupFieldValidationObservables
      .weight_variance_threshold_percent
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .require_container_type
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .require_complete_all_instructions
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .can_continue_closed_sessions
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .auto_print_reports_on_complete
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
  }

  close() {
    this.$finish.emit();
  }

  openImageViewer(imageSource: string) {
    this.$shell.openImageViewerDialog(imageSource);
  }
 
  //#region private flows
  on_init(event = null) {
    return this.on_initInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_initInternal(
    $form: PackVerification_configurations_formComponent,
  
    $shell: PackVerification_ShellService,
    $datasources: PackVerification_DatasourceService,
    $flows: PackVerification_FlowService,
    $reports: PackVerification_ReportService,
    $settings: SettingsValuesService,
    $operations: PackVerification_OperationService,
    $utils: UtilsService,
    $context: PackVerification_configurations_form_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: PackVerification_LocalizationService,
    $event: any
  ) {
  $form.outParams.is_confirmed = false;
  
  // Get configurations 
  const configResult = (await $flows.PackVerification.get_configurations_flow({}));
  
  if ($utils.isDefined(configResult.reasons)) {
      $shell.PackVerification.openToaster(`Error getting configuration values`, configResult.reasons.join(', '), EToasterType.Error, { disableTimeOut: true, closeButton: true, positionClass: EToasterPosition.bottomRight });
      $form.close();
      return;
  }
  
  const config = configResult.configurations;
  $form.vars.entity = config;
  
  $form.fields.auto_print_reports_on_complete.control.value = config.auto_print_reports_on_complete;
  $form.fields.can_continue_closed_sessions.control.value = config.can_continue_closed_sessions;
  $form.fields.require_complete_all_instructions.control.value = config.require_complete_all_instructions;
  $form.fields.require_container_type.control.value = config.require_container_type;
  $form.fields.weight_variance_threshold_percent.control.value = config.weight_variance_threshold_percent;
  }
  on_confirm_clicked(event = null) {
    return this.on_confirm_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_confirm_clickedInternal(
    $form: PackVerification_configurations_formComponent,
  
    $shell: PackVerification_ShellService,
    $datasources: PackVerification_DatasourceService,
    $flows: PackVerification_FlowService,
    $reports: PackVerification_ReportService,
    $settings: SettingsValuesService,
    $operations: PackVerification_OperationService,
    $utils: UtilsService,
    $context: PackVerification_configurations_form_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: PackVerification_LocalizationService,
    $event: any
  ) {
  await $flows.PackVerification.set_configurations_flow({
      configurations: {
          require_complete_all_instructions: $form.fields.require_complete_all_instructions.control.value,
          require_container_type: $form.fields.require_container_type.control.value,
          weight_variance_threshold_percent: $form.fields.weight_variance_threshold_percent.control.value,
          can_continue_closed_sessions: $form.fields.can_continue_closed_sessions.control.value,
          auto_print_reports_on_complete: $form.fields.auto_print_reports_on_complete.control.value,
      }
  });
  
  $form.outParams.is_confirmed = true;
  $form.close();
  }
  on_cancel_clicked(event = null) {
    return this.on_cancel_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_cancel_clickedInternal(
    $form: PackVerification_configurations_formComponent,
  
    $shell: PackVerification_ShellService,
    $datasources: PackVerification_DatasourceService,
    $flows: PackVerification_FlowService,
    $reports: PackVerification_ReportService,
    $settings: SettingsValuesService,
    $operations: PackVerification_OperationService,
    $utils: UtilsService,
    $context: PackVerification_configurations_form_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: PackVerification_LocalizationService,
    $event: any
  ) {
  $form.close();
  }
  on_value_change(event = null) {
    return this.on_value_changeInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_value_changeInternal(
    $form: PackVerification_configurations_formComponent,
  
    $shell: PackVerification_ShellService,
    $datasources: PackVerification_DatasourceService,
    $flows: PackVerification_FlowService,
    $reports: PackVerification_ReportService,
    $settings: SettingsValuesService,
    $operations: PackVerification_OperationService,
    $utils: UtilsService,
    $context: PackVerification_configurations_form_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: PackVerification_LocalizationService,
    $event: any
  ) {
    this.$logger.log('PackVerification', 'configurations_form.on_value_change');
  
  }
  //#endregion private flows
  //#region validation flows
  
  validate_form(fieldErrors: { weight_variance_threshold_percent: Pick<string[], 'push'>,require_container_type: Pick<string[], 'push'>,require_complete_all_instructions: Pick<string[], 'push'>,can_continue_closed_sessions: Pick<string[], 'push'>,auto_print_reports_on_complete: Pick<string[], 'push'>, } = null) {
    const emptyResult = { weight_variance_threshold_percent:[],require_container_type:[],require_complete_all_instructions:[],can_continue_closed_sessions:[],auto_print_reports_on_complete:[], };
    if (!this.initialized){
      return Promise.resolve(emptyResult);
    }
    return this.validate_formInternal(
      this,
      { fieldErrors: fieldErrors ?? emptyResult },
      this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization
      this.$context
      );
  }
  async validate_formInternal(
    $form: PackVerification_configurations_formComponent,
    $validation:{
      fieldErrors: { weight_variance_threshold_percent: Pick<string[], 'push'>,require_container_type: Pick<string[], 'push'>,require_complete_all_instructions: Pick<string[], 'push'>,can_continue_closed_sessions: Pick<string[], 'push'>,auto_print_reports_on_complete: Pick<string[], 'push'>, }
    },
    $shell: PackVerification_ShellService,
    $datasources: PackVerification_DatasourceService,
    $flows: PackVerification_FlowService,
    $reports: PackVerification_ReportService,
    $settings: SettingsValuesService,
    $operations: PackVerification_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: PackVerification_LocalizationService,
    $context: PackVerification_configurations_form_ComponentContextService
  ) {
    let hasChangedValue = false;
  let allHaveValue = true;
  
  for (let key in $form.vars.entity) {
      if (!$utils.isDefined($form.fields[key].control.value)) {
          allHaveValue = false;
      }
  
      if ($form.fields[key].control.value !== $form.vars.entity[key]) {
          hasChangedValue = true;
      }
  }
  
  $form.toolbar.confirm.control.readOnly = !(hasChangedValue && allHaveValue);
    return $validation.fieldErrors as { weight_variance_threshold_percent:[],require_container_type:[],require_complete_all_instructions:[],can_continue_closed_sessions:[],auto_print_reports_on_complete:[], };
  }
  //#endregion validation flows
}
